import React, { type PropsWithChildren } from 'react';
import { StyleSheet } from 'react-native';
import { LoadingPlaceholder, theme, useResponsive } from '@sg/garnish';

import { StripePaymentProvider } from '@order/integrations/stripe';
import { getEnvVars } from '@order/utils';

import { NotFoundView } from '../../../../../../components';
import { type GiftCardCheckoutFormReturn } from '../../../../form';
import { GiftCardCheckoutFormContainer } from '../../components';
import { useCustomerPaymentInfoQuery } from '../../GraphQL/CustomerPaymentInfo.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutPaymentProvider = (
  props: GiftCardCheckoutPaymentProviderProps,
) => {
  const { form, children, purchaseAttempt } = props;

  const { match } = useResponsive();
  const loadingContainerStyles = [
    styles.loadingContainer,
    match([null, styles.loadingContainerSM]),
  ];

  const [{ data, error, fetching }] = useCustomerPaymentInfoQuery({
    requestPolicy: 'network-only',
  });

  const amount = form.watch('amount');
  const stripeCustomerId = data?.customerPaymentInfo?.id;
  const stripeCustomerKey = data?.customerPaymentInfo?.paymentEphemeralKey;
  const stripeSession = data?.customerPaymentInfo?.paymentSessionId;

  const hasStripeKeys = stripeCustomerId && stripeCustomerKey && stripeSession;

  if (fetching) {
    return (
      <GiftCardCheckoutFormContainer style={loadingContainerStyles}>
        <LoadingPlaceholder
          rows={6}
          rowHeight={[160, 400, 160, 400, 400, 160]}
          palette="cream"
        />
      </GiftCardCheckoutFormContainer>
    );
  }

  if (error || !hasStripeKeys) {
    return <NotFoundView />;
  }

  return (
    <StripePaymentProvider
      purchaseAttempt={purchaseAttempt}
      publicKey={stripePublicKey}
      amount={amount}
      sessionId={stripeSession}
    >
      {children as React.ReactElement}
    </StripePaymentProvider>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    backgroundColor: theme.colors.APP_BACKGROUND,
    padding: theme.spacing['4'],
  },
  loadingContainerSM: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const stripePublicKey = getEnvVars().STRIPE_PUBLIC_KEY;

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardCheckoutPaymentProviderProps = PropsWithChildren & {
  purchaseAttempt: number;
  form: GiftCardCheckoutFormReturn;
};
